/**
 * Geepas Warranty & Cart Bundle Synchronization
 * Handles 1-to-1 appliance & warranty pairing, atomic removal, and orphan cleanup.
 */

(function() {
  'use strict';

  let isPerformingCartSync = false;

  function getRootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCartUrl() {
    const root = getRootUrl();
    return root.endsWith('/') ? root + 'cart.js' : root + '/cart.js';
  }

  function getCartUpdateUrl() {
    const root = getRootUrl();
    return root.endsWith('/') ? root + 'cart/update.js' : root + '/cart/update.js';
  }

  // Direct Click Interception for Remove Buttons to remove parent and warranty atomically
  document.addEventListener('click', async function(event) {
    const removeBtn = event.target.closest('.line-item__remove-button');
    if (!removeBtn) return;

    const lineItemEl = removeBtn.closest('.line-item') || removeBtn.closest('line-item') || removeBtn.closest('tr');
    const bundleId = removeBtn.getAttribute('data-bundle-id') || (lineItemEl && lineItemEl.getAttribute('data-bundle-id'));
    const isWarranty = (removeBtn.getAttribute('data-is-warranty') === 'true') || (lineItemEl && lineItemEl.getAttribute('data-is-warranty') === 'true');
    const itemKey = removeBtn.getAttribute('data-key') || (lineItemEl && lineItemEl.getAttribute('data-key'));

    // If removing a warranty individually, allow standard single-item removal
    if (isWarranty) {
      return;
    }

    // If removing a physical product that has a bundle ID or warranty attached:
    if (bundleId || itemKey) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        // Show loader on the item
        const loader = lineItemEl ? lineItemEl.querySelector('.line-item__loader') : null;
        if (loader) loader.hidden = false;

        // Fetch current cart to get exact line keys
        const cartResp = await fetch(getCartUrl());
        const cart = await cartResp.json();
        const updates = {};

        if (itemKey) {
          updates[itemKey] = 0;
        }

        // Find sibling warranty in cart
        if (cart && Array.isArray(cart.items)) {
          for (const it of cart.items) {
            const itBundleId = it.properties ? it.properties._warranty_bundle_id : null;
            const itIsWarranty = it.handle === 'extended-warranty-3-years' ||
                                 it.product_type === 'Warranty Service' ||
                                 Boolean(it.properties && it.properties._protects_product_id);

            if (bundleId && itBundleId === bundleId) {
              updates[it.key] = 0;
            }
          }
        }

        // Send atomic update removing both
        const updateResp = await fetch(getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            updates: updates,
            sections: ['mini-cart', 'main-cart']
          })
        });

        const newCart = await updateResp.json();

        // Refresh cart drawer and page
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: newCart,
            openMiniCart: window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType === 'drawer'
          }
        }));

        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: newCart }
        }));

        // If on /cart page, reload if needed
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        }
      } catch (err) {
        console.error('Error removing bundled items:', err);
        // Fallback to default link navigation if fetch fails
        window.location.href = removeBtn.getAttribute('href');
      }
    }
  }, true); // Capture phase to take priority

  // Automatic Orphan Cleanup Handler
  async function cleanupOrphanedWarranties(cart) {
    if (isPerformingCartSync) return;

    try {
      if (!cart || !Array.isArray(cart.items)) {
        const resp = await fetch(getCartUrl());
        cart = await resp.json();
      }

      if (!cart || !Array.isArray(cart.items)) return;

      const physicalBundleIds = new Set();
      const physicalProductIds = new Set();

      for (const item of cart.items) {
        const isWarranty = item.handle === 'extended-warranty-3-years' ||
                           item.product_type === 'Warranty Service' ||
                           Boolean(item.properties && item.properties._protects_product_id);
        if (!isWarranty) {
          if (item.properties && item.properties._warranty_bundle_id) {
            physicalBundleIds.add(String(item.properties._warranty_bundle_id).trim());
          }
          physicalProductIds.add(String(item.product_id));
        }
      }

      const updates = {};
      let hasOrphans = false;

      for (const item of cart.items) {
        const isWarranty = item.handle === 'extended-warranty-3-years' ||
                           item.product_type === 'Warranty Service' ||
                           Boolean(item.properties && item.properties._protects_product_id);

        if (isWarranty) {
          const bId = item.properties && item.properties._warranty_bundle_id ? String(item.properties._warranty_bundle_id).trim() : '';
          const pId = item.properties && item.properties._protects_product_id ? String(item.properties._protects_product_id).trim() : '';

          const isOrphan = (bId && !physicalBundleIds.has(bId)) ||
                           (!bId && pId && !physicalProductIds.has(pId)) ||
                           (!bId && !pId);

          if (isOrphan) {
            updates[item.key] = 0;
            hasOrphans = true;
          }
        }
      }

      if (hasOrphans) {
        isPerformingCartSync = true;
        const res = await fetch(getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            updates: updates,
            sections: ['mini-cart', 'main-cart']
          })
        });
        const updatedCart = await res.json();

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: updatedCart }
        }));
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: updatedCart }
        }));
      }
    } catch (e) {
      console.error('Error in cleanupOrphanedWarranties:', e);
    } finally {
      isPerformingCartSync = false;
    }
  }

  // Listen on both document and document.documentElement for all cart lifecycle events
  document.addEventListener('cart:updated', function(e) {
    if (e && e.detail && e.detail.cart) {
      cleanupOrphanedWarranties(e.detail.cart);
    }
  });

  document.documentElement.addEventListener('cart:updated', function(e) {
    if (e && e.detail && e.detail.cart) {
      cleanupOrphanedWarranties(e.detail.cart);
    }
  });

  // Run on initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      cleanupOrphanedWarranties();
    });
  } else {
    cleanupOrphanedWarranties();
  }
})();